import { Link } from 'react-router-dom';
import { ArrowRight, GitCompare, Calendar, Pin, Share2 } from 'lucide-react';
import './landing.css';

/**
 * Concept 3 — INSPECTOR.
 * Product-Hunt-style screenshot showcase. Light bg, big hero
 * gradient headline, then 4 sections each centered on a real
 * feature mock-up with floating annotation tags pointing at parts
 * of the UI. Linear/Stripe/Notion-modern.
 */
export default function Landing3() {
  return (
    <div className="lp3 lp-shell" data-testid="landing-3">
      {/* LandingSwitcher removed Round 91l. */}

      <header className="max-w-6xl mx-auto px-6 pt-24 pb-32">
        <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-600 font-bold mb-4">
          Built for dividend-only investors · Now in public beta
        </div>
        <h1 className="hero-title" data-testid="lp3-title">
          Track every dividend.<br />
          <span className="grad">Verify every date.</span>
        </h1>
        <p className="mt-6 text-xl text-zinc-600 max-w-2xl leading-relaxed">
          DiviDrip pulls live data from Massive&apos;s exchange feeds. So your ex-div and pay dates are sourced — not estimated.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/" className="lp-btn lp-btn-primary-3" data-testid="lp3-cta-primary">
            Try it now <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#features" className="lp-btn border border-zinc-300 text-zinc-700 hover:bg-white">
            See the features
          </a>
        </div>
      </header>

      {/* Feature 1: Compare modal */}
      <section id="features" className="bg-white py-24 border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <GitCompare className="w-8 h-8 text-fuchsia-600 mb-4" />
            <h2 className="text-4xl font-bold tracking-tight mb-4">Compare any two tickers, side-by-side.</h2>
            <p className="text-zinc-600 leading-relaxed mb-4 text-lg">
              3 years of yield history. Crossover diamonds. Optional log scale when one ticker dwarfs the other. Pin the face-offs that matter so they&apos;re one click away on your Watchlist.
            </p>
            <ul className="space-y-2 text-zinc-700 text-sm">
              <li>✓ Y-axis scale legend with skew warning</li>
              <li>✓ Crossover detection (where one ticker overtakes another)</li>
              <li>✓ One-line memo per saved pair</li>
            </ul>
          </div>
          <div className="relative mock p-6">
            <div className="flex items-center justify-between text-xs text-zinc-500 mb-4">
              <span className="font-mono font-bold">YIELD % HISTORY (3 YEARS)</span>
              <span className="font-mono">Y-axis: 0% → 3.2% · <span className="text-amber-600">9.1× skew</span></span>
            </div>
            <div className="relative h-44 bg-gradient-to-b from-zinc-50 to-white rounded border border-zinc-100 overflow-hidden">
              <svg viewBox="0 0 400 160" className="w-full h-full">
                <path d="M0 30 Q 100 20 200 40 T 400 50" stroke="#0ea5e9" strokeWidth="2.5" fill="none" />
                <path d="M0 140 Q 100 138 200 142 T 400 145" stroke="#8b5cf6" strokeWidth="2.5" fill="none" />
                <circle cx="200" cy="40" r="5" fill="#fbbf24" />
              </svg>
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs font-mono">
              <span className="text-sky-600">● NATH 2.81%</span>
              <span className="text-violet-600">● PEB 0.31%</span>
            </div>
            <div className="annotation" style={{ top: 90, right: -20 }}>
              ⬥ Crossover marker shows where lines flip
              <span style={{ position: 'absolute', left: -5, top: 14 }}>◀</span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 2: Dividend Calendar */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative mock p-6">
            <div className="text-xs font-mono font-bold text-zinc-500 mb-4">DIVIDEND CALENDAR · FEB 2026</div>
            <div className="grid grid-cols-7 gap-1 text-xs">
              {Array.from({ length: 28 }).map((_, i) => {
                const day = i + 1;
                const isPayout = [5, 12, 14, 19, 26].includes(day);
                return (
                  <div key={i} className={`aspect-square rounded flex flex-col items-center justify-center border ${isPayout ? 'bg-emerald-100 border-emerald-300 text-emerald-800 font-bold' : 'bg-zinc-50 border-zinc-100 text-zinc-400'}`}>
                    {day}
                    {isPayout && <span className="text-[8px]">PAY</span>}
                  </div>
                );
              })}
            </div>
            <div className="annotation" style={{ bottom: 70, right: -10, maxWidth: 200 }}>
              Massive-locked. Not estimates.
            </div>
          </div>
          <div>
            <Calendar className="w-8 h-8 text-emerald-600 mb-4" />
            <h2 className="text-4xl font-bold tracking-tight mb-4">Pay dates that match the exchange.</h2>
            <p className="text-zinc-600 leading-relaxed mb-4 text-lg">
              We pull from Massive&apos;s exchange feed and lock the dates in our DB. 99.7% of dividend-paying tickers have verified dates today.
            </p>
            <div className="text-xs font-mono text-zinc-500">
              <span className="text-emerald-600 font-bold">✓ VERIFIED</span> 4,582 tickers ·
              <span className="text-zinc-400"> ESTIMATED</span> 16 (Massive doesn&apos;t cover yet)
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3: Pinned Pairs */}
      <section className="bg-white py-24 border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Pin className="w-8 h-8 text-amber-500 mb-4" />
            <h2 className="text-4xl font-bold tracking-tight mb-4">Pin your favorite face-offs.</h2>
            <p className="text-zinc-600 leading-relaxed text-lg">
              Save up to 15 ticker pairs you keep coming back to. Each one lives on your Watchlist tab as a mini face-off card with live yields, with a one-line memo so you remember why you starred it.
            </p>
          </div>
          <div className="space-y-2">
            {[
              ['NATH', 'TRIN', '2.81%', '7.92%', 'watching post-earnings drop'],
              ['JEPI', 'JEPQ', '5.38%', '4.12%', 'roth target — which monthly?'],
              ['SCHD', 'VYM', '3.45%', '3.18%', 'core holding rebalance'],
            ].map(([a, b, ya, yb, note]) => (
              <div key={a} className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Pin className="w-3 h-3 text-amber-500" />
                  <span className="font-mono font-bold text-sky-600">{a}</span>
                  <span className="text-zinc-400 text-xs">vs</span>
                  <span className="font-mono font-bold text-violet-600">{b}</span>
                  <span className="ml-auto text-xs font-mono">
                    <span className="text-sky-600">{ya}</span> · <span className="text-violet-600">{yb}</span>
                  </span>
                </div>
                <div className="text-xs italic text-amber-700 truncate">&ldquo;{note}&rdquo;</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature 4: Triangle */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative mock p-8 flex items-center justify-center bg-gradient-to-br from-violet-50 to-fuchsia-50">
            <svg viewBox="0 0 200 180" className="w-full max-w-xs">
              <polygon points="100,20 180,160 20,160" stroke="#8b5cf6" strokeWidth="2" fill="rgba(139,92,246,0.06)" />
              <text x="100" y="14" textAnchor="middle" fontSize="14" fontWeight="800" fill="#7c3aed">JEPI</text>
              <text x="185" y="170" fontSize="14" fontWeight="800" fill="#ec4899">SCHD</text>
              <text x="15" y="170" fontSize="14" fontWeight="800" fill="#f97316">NATH</text>
              <circle cx="100" cy="20" r="5" fill="#7c3aed" />
              <circle cx="180" cy="160" r="5" fill="#ec4899" />
              <circle cx="20" cy="160" r="5" fill="#f97316" />
              <text x="100" y="100" textAnchor="middle" fontSize="9" fill="#71717a">Yield · 5y CAGR · Quality</text>
            </svg>
          </div>
          <div>
            <Share2 className="w-8 h-8 text-violet-600 mb-4" />
            <h2 className="text-4xl font-bold tracking-tight mb-4">Triangle Compare. Shareable card.</h2>
            <p className="text-zinc-600 leading-relaxed text-lg">
              Three tickers, three axes, one beautiful card. Tweet it, post to Reddit, paste in Discord — the link unfurls into a rich preview with the exact triangle chart you saw on screen.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-12 text-center text-xs text-zinc-500">
        © 2026 DiviDrip · Made for dividend investors
      </footer>
    </div>
  );
}
